/**
 * CustomerHub.jsx — Phase 12 Cutover-ready.
 *
 * The unified 5-tab customer section (replaces 9 legacy tabs). Sits at
 * /customer-hub-preview until the owner flips REACT_APP_HUB_V2_ENABLED
 * to production-mount. The 4 previously-placeholder tabs now embed
 * their real production components (POSCustomers, ExpectedToday,
 * CustomerLab, CustomerSurveys) so migration is content-preserving.
 *
 * Phase 12 additions:
 *   - MeasuredMetrics header (Conversion / Retention 30d / AOV).
 *   - UnifiedTargeting panel (segment + cohort + paid-only + count + CSV).
 *   - Role gate: projected/predicted numbers only for CEO/admin.
 *   - Direct link to /data-sources for every metric shown.
 */
import React, { Suspense, lazy, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import SegmentTimeline from "./SegmentTimeline";
import MovementMap from "./MovementMap";
import MeasuredMetrics from "./MeasuredMetrics";
import RetentionRateCard from "./RetentionRateCard";
import NewRegistrationsCard from "./NewRegistrationsCard";
import RepeatRateCard from "./RepeatRateCard";
import RecoveryRateCard from "./RecoveryRateCard";
import ReadyStatesRow from "./ReadyStatesRow";
import RegisteredNoVisitCard from "./RegisteredNoVisitCard";
import PredictionAccuracyCard from "./PredictionAccuracyCard";
import { CustomerFilterProvider, CustomerFilterBar, useCustomerFilter } from "./CustomerFilterContext";
import { InfoTip } from "../components/InfoTip";
import "./hub_responsive.css";

// Lazy-load the four legacy content owners so their heavy code splits
// don't block the segments tab (which is the default).
const POSCustomers    = lazy(() => import("../POSCustomers"));
const ExpectedToday   = lazy(() => import("../ExpectedToday"));
const CustomerLab     = lazy(() => import("../CustomerLab"));
const CustomerSurveys = lazy(() => import("../CustomerSurveys"));
// v200.48 (2026-08-13) — merged the legacy «شرائح العملاء» page into
// the «directory» sub-tab. Header (source banner + hero KPI + campaign
// impact) sourced live from /api/customers/directory. CustomersSmart
// preserves the full smart directory (search by name/phone + segment
// pills + drill-down + smart-action modal + blast modal).
const LegacyCustomersHeader = lazy(() =>
  import("../Customers").then(m => ({ default: m.LegacyCustomersHeader })));
const CustomersSmart = lazy(() => import("../CustomersSmart"));
const SaudiMarketInsights = lazy(() => import("../SaudiMarketInsights"));
const SendCapApprovalsInbox = lazy(() => import("../SendCapApprovalsInbox"));

const TABS = [
  { id: "segments",  emoji: "\u{1F3AF}", label: "تحليلات الشرائح",
    subtitle: "الشرائح الثمانية + خريطة الحركة + بطاقات الإجراء" },
  { id: "directory", emoji: "\u{1F4C7}", label: "دليل العملاء",
    subtitle: "بحث + تصفية + بطاقة العميل الكاملة" },
  { id: "expected",  emoji: "\u{1F52E}", label: "الزوار المتوقعون",
    subtitle: "من نتوقع زيارته اليوم مع دقة النموذج" },
  { id: "lab",       emoji: "\u{1F9EA}", label: "مختبر التحليلات",
    subtitle: "تحليلات معمّقة: مقارنة فترتين، تجمعات، نمط الشهر…" },
  { id: "comms",     emoji: "\u{1F4CB}", label: "استبيانات العملاء",
    subtitle: "إرسال استبيانات + سجل التواصل الموحّد" },
  { id: "cap_approvals", emoji: "\u{1F514}", label: "موافقات تجاوز السقف",
    subtitle: "طلبات تجاوز سقف الإرسال المعلّقة — اعتماد/رفض" },
];

// v201.5 (2026-06) — page access is governed SOLELY by the registered RBAC
// permission `customers.view` (see rbac.py). No hardcoded role names live in
// this display gate anymore: whichever role a tenant maps to `customers.view`
// (marketing, marketing_staff, advisor, or any future/custom role) sees the
// FULL hub with all sub-tabs. Fine-grained control stays at the feature level
// inside each pane, never by hiding the page or a sub-tab.

export default function CustomerHub() {
  // Phase 7h — لا Provider عام بعد الآن: كل تبويب يلفّ محتواه بـ
  // CustomerFilterProvider مستقل (فلتر مستقل لكل تبويب).
  return <CustomerHubBody />;
}

function LayerHeader({ index, title, subtitle, action }) {
  return (
    <div data-testid={`dir-layer-${index}`} dir="rtl" style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      margin: "30px 0 14px", paddingInlineStart: 14,
      borderInlineStart: "4px solid #065F46",
    }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#3D2A1C" }}>{title}</div>
        <div style={{ fontSize: 12, color: "#78716C", marginTop: 2 }}>{subtitle}</div>
      </div>
      {action}
    </div>
  );
}

function DirectoryExportButton() {
  const [busy, setBusy] = useState(false);
  const [seg, setSeg] = useState("all");
  const [segOptions, setSegOptions] = useState([]);
  // 2026-08 (إصلاح «التصدير لا يحترم الفلتر») — يقرأ الفرع/المدى من الشريط
  // المشترك لتبويب الدليل (نفس المصدر الذي تعرضه الشاشة، لا فلتر محلي منفصل).
  const filterCtx = useCustomerFilter();
  const branchId = filterCtx?.branchId || "";
  const manualActive = filterCtx?.manualActive || false;
  const rangeFrom = manualActive ? filterCtx?.from : "";
  const rangeTo = manualActive ? filterCtx?.to : "";
  useEffect(() => {
    let cancelled = false;
    api.get("/segments/definitions")
      .then((r) => {
        if (cancelled) return;
        const defs = r.data?.definitions || [];
        setSegOptions(defs.map((d) => ({ code: d.code, label: d.label_ar || d.code })));
      })
      .catch(() => { /* القائمة تبقى «الكل» فقط عند الفشل */ });
    return () => { cancelled = true; };
  }, []);
  const run = async () => {
    setBusy(true);
    try {
      // segment=all افتراضيًا، أو كود شريحة قانوني — من نفس مصدر «دليل العملاء».
      // branch_id/from/to اختياريان: عند عدم وجود تجاوز يدوي للتاريخ يبقى
      // التصدير على الفترة الكاملة (كل التاريخ) كما كان سابقاً.
      const params = { segment: seg };
      if (branchId) params.branch_id = branchId;
      if (rangeFrom) params.from = rangeFrom;
      if (rangeTo) params.to = rangeTo;
      const res = await api.get("/customers/directory/export",
        { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : "customers_directory.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const rows = res.headers?.["x-export-rows"];
      const segName = seg === "all" ? "كل العملاء" : (segOptions.find((o) => o.code === seg)?.label || seg);
      toast.success(`تم تصدير ${rows ?? ""} صف (${segName}) إلى Excel`);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("لا تملك صلاحية تصدير قائمة العملاء");
      else toast.error("تعذّر التصدير — حاول مجددًا");
    } finally { setBusy(false); }
  };
  return (
    <div dir="rtl" style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-start", marginBottom: 12, flexWrap: "wrap" }}>
      <select
        data-testid="export-segment-select"
        value={seg}
        onChange={(e) => setSeg(e.target.value)}
        disabled={busy}
        style={{
          border: "1px solid rgba(6,95,70,0.30)", background: "#fff", color: "#065F46",
          borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 600,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        <option value="all">الكل (كل الشرائح)</option>
        {segOptions.map((o) => (
          <option key={o.code} value={o.code}>{o.label}</option>
        ))}
      </select>
      <button
        type="button"
        data-testid="btn-export-customers-directory"
        onClick={run}
        disabled={busy}
        style={{
          border: "1px solid rgba(6,95,70,0.30)", background: busy ? "#E7E5E4" : "#065F46",
          color: busy ? "#78716C" : "#fff", borderRadius: 999, padding: "9px 20px",
          fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer",
          whiteSpace: "nowrap", transition: "background-color 150ms ease",
        }}
      >
        {busy ? "… جارٍ التصدير" : "⬇️ تصدير Excel"}
      </button>
      {(branchId || rangeFrom) && (
        <span data-testid="export-filter-active-hint" style={{ fontSize: 11, color: "#78716C" }}>
          🔎 سيُطبَّق فلتر الشاشة الحالي (فرع/مدى) على التصدير
        </span>
      )}
    </div>
  );
}

function CustomerHubBody() {
  const { hasPerm, permissions, loadPermissions } = useAuth();
  const [auth, setAuth] = useState({ loading: true, user: null, error: null });
  // Phase 12 — legacy notif routing: if the notification bell stashed a
  // sub-tab id, open the hub on that pane (then clear it).
  const [tab, setTab] = useState(() => {
    try {
      const s = localStorage.getItem("hub.default_sub_tab");
      if (s) {
        localStorage.removeItem("hub.default_sub_tab");
        return s;
      }
    } catch (_e) { /* storage disabled */ }
    return "segments";
  });
  const [dist, setDist] = useState(null);
  // A3 (البند 3ج) — أُزيل مفتاح «المدفوع/كل الزيارات»: مدفوعة فقط دائماً،
  // نفس القرار الموحَّد المستخدَم في كل شاشة عميل أخرى بالفعل.
  // Phase 7f — طبقة «سجل رتم الكامل» مطوية افتراضيًا في تبويب الدليل.
  const [rtmLogOpen, setRtmLogOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setAuth({ loading: false, user: r.data, error: null }))
      .catch((e) => setAuth({
        loading: false, user: null,
        error: e?.response?.status === 401 ? "unauthenticated" : (e?.message || "خطأ"),
      }));
    // Ensure the registered RBAC permission set is loaded for the gate below.
    if (typeof loadPermissions === "function") loadPermissions();
  }, [loadPermissions]);

  useEffect(() => {
    if (tab !== "segments" || !auth.user) return;
    // Phase 7c/7g: شعار الشروط يقرأ التوزيع من نافذة تحليلية ثابتة 90 يومًا.
    // (بطاقات الحملات نُقِلت إلى مركز الحملات → «توصيات ذكية».)
    api.get("/segments/distribution", { params: { window_days: 90, paid_only: true } })
      .then((r) => setDist(r.data)).catch(() => setDist(null));
  }, [tab, auth.user]);

  const role = auth.user?.role;
  // v201.5 — RBAC-driven page gate (registered permission, not a role list).
  // `hasPerm` returns true for admin automatically and for any role a tenant
  // maps to `customers.view`. Permissions load async → treat null as "loading"
  // to avoid a false "forbidden" flash for non-admin roles.
  const canView = hasPerm("customers.view");
  const permsLoading = role !== "admin" && permissions === null;
  const isCeoView = role === "admin" || role === "ceo";

  // Registered-permission holders see the FULL hub (all sub-tabs). No per-tab
  // role gating — fine-grained control lives at the feature level inside panes.
  const visibleTabs = TABS;

  // If the current `tab` isn't visible to this role, default to first visible.
  const activeTab = visibleTabs.some((t) => t.id === tab)
    ? tab
    : (visibleTabs[0]?.id || "segments");

  return (
    <div
      dir="rtl"
      data-testid="customer-hub-preview"
      className="hub-root"
      style={{
        minHeight: "100vh",
        background: "#F7F2EC",
        color: "#3D2A1C",
        fontFamily: "'El Messiri', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Noto Naskh Arabic', sans-serif",
        fontVariantNumeric: "lining-nums tabular-nums",
        padding: "40px 24px",
      }}
    >
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <header style={{ marginBottom: 28 }}>
          <div className="hub-header-row" style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <span style={{
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(170,149,102,0.14)", color: "#AA9566",
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            }}>
              PHASE 12 · CUTOVER-READY
            </span>
            <h1 className="hub-h1" style={{ margin: 0, fontSize: 28, fontWeight: 700,
                          letterSpacing: -0.6, color: "#3D2A1C" }}>
              مركز العملاء الموحّد
            </h1>
            <a
              href="/data-sources"
              data-testid="hub-data-sources-link"
              className="hub-ds-link"
              target="_blank"
              rel="noreferrer"
              style={{
                marginInlineStart: "auto",
                padding: "6px 12px", borderRadius: 999,
                background: "rgba(15,118,110,0.10)",
                color: "#0F766E",
                fontSize: 12, fontWeight: 700,
                textDecoration: "none",
                border: "1px solid rgba(15,118,110,0.35)",
              }}
            >
              🔎 مصادر الأرقام
            </a>
          </div>
          <p style={{ marginTop: 10, fontSize: 14, color: "#78716C" }}>
            الأرقام المقيسة تُعرض للفريق. الأرقام المُسقَطة والمتوقَّعة
            {isCeoView ? " مرئية لأنك CEO/admin." : " مخفية لأنها ليست حقائق مؤكَّدة."}
          </p>
          {dist?.consistency_meta?.excluded_free_visits != null && (
            <p data-testid="hub-excluded-free-visits-line" style={{ marginTop: 6, fontSize: 12.5, color: "#92400E", fontWeight: 600 }}>
              استُبعدت {Number(dist.consistency_meta.excluded_free_visits).toLocaleString("en-US")} زيارة مجانية/مخصومة بالكامل في الفترة
            </p>
          )}
        </header>

        {(auth.loading || permsLoading) && (
          <div data-testid="hub-loading"
               style={{ padding: 40, textAlign: "center", color: "#78716C" }}>
            جارٍ التحقق من الصلاحية…
          </div>
        )}

        {!auth.loading && auth.error === "unauthenticated" && (
          <div data-testid="hub-unauth" style={{
            padding: 24, borderRadius: 16,
            background: "#FEF3F2", border: "1px solid rgba(185,28,28,0.35)",
            color: "#B91C1C",
          }}>
            يجب تسجيل الدخول أولًا. افتح <code style={{
              background: "rgba(0,0,0,0.05)", padding: "2px 6px", borderRadius: 4,
            }}>/</code> وسجّل ثم عُد إلى هذه الصفحة.
          </div>
        )}

        {!auth.loading && !permsLoading && auth.user && !canView && (
          <div data-testid="hub-forbidden" style={{
            padding: 24, borderRadius: 16,
            background: "#FEF3F2", border: "1px solid rgba(185,28,28,0.35)",
            color: "#B91C1C",
          }}>
            هذه الصفحة تتطلب صلاحية «عرض العملاء» (customers.view). اطلبها من
            مدير النظام عبر شاشة «الدور والصفحات».
            (دورك الحالي: {role || "غير معروف"})
          </div>
        )}

        {!auth.loading && !permsLoading && canView && (
          <>
            <div className="hub-tabs-wrap" style={{ position: "relative", marginBottom: 24 }}>
              <nav
                data-testid="hub-tabs"
                className="hub-tabs"
                style={{
                  display: "flex", gap: 8, flexWrap: "wrap",
                  padding: 8,
                  /* Phase 7h — خلفية شريط التبويبات: بيج البراند المعتمد
                     (المصدر: index.css:40 --beige #F3EBDD) مع حدّ ذهبي خفيف
                     rgba(170,149,102,0.35) لتمييز الشريط عن البطاقات البيضاء. */
                  background: "#F3EBDD",
                  borderRadius: 14, border: "1px solid rgba(170,149,102,0.35)",
                  boxShadow: "0 4px 16px rgba(61,42,28,0.06)",
                }}
              >
                {visibleTabs.map((t) => {
                  const active = t.id === activeTab;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`hub-tab-${t.id}`}
                      onClick={() => setTab(t.id)}
                      style={{
                        border: "none", cursor: "pointer",
                        padding: "10px 16px", borderRadius: 10, fontSize: 13,
                        fontWeight: active ? 700 : 500,
                        background: active ? "rgba(170,149,102,0.14)" : "transparent",
                        color: active ? "#AA9566" : "#57534E",
                        transition: "background 200ms ease, color 200ms ease",
                      }}
                    >
                      {t.emoji} {t.label}
                    </button>
                  );
                })}
              </nav>
              {/* Mobile-only scroll hint. Sits OUTSIDE the scrolling
                  nav (as a sibling) so it stays visually pinned to
                  the leading edge as tabs scroll. Hidden by CSS on
                  ≥768px. Decorative → aria-hidden. */}
              <span
                className="hub-tabs-hint"
                data-testid="hub-tabs-scroll-hint"
                aria-hidden="true"
              >
                ‹
              </span>
            </div>

            <section data-testid={`hub-panel-${activeTab}`}>
              {activeTab === "segments" && (
                <CustomerFilterProvider>
                  {/* فلتر مستقل لتبويب الشرائح — بلا فرع (team-summary لا يدعمه) */}
                  <CustomerFilterBar showBranch={false} />

                  {/* Phase 12 mandate #5 — MEASURED metrics header (team-visible) */}
                  <MeasuredMetrics windowDays={90} />

                  {/* «البوابة قبل النشر» 2026-10-13 (بند 2أ) — صف الحالات
                       الجاهزة الخمس، كل زر يفتح CustomerListModal بنفس العدد. */}
                  <ReadyStatesRow />

                  {/* Phase 7h — نُقِل «الاستهداف الموحّد» و«بطاقات الإجراء»
                       (UnifiedTargeting + ActionInsights) إلى مركز الحملات →
                       تبويب «توصيات ذكية». */}
                  <SegmentTimeline windowDays={90} />

                  {/* جولة تصحيح 2026-10-13 (بند 3ج) — بجانب الخريطة
                       الزمنية، لا داخلها؛ فئة مستقلة عن الشرائح تماماً. */}
                  <RegisteredNoVisitCard />
                  <MovementMap />

                  {/* v200.70 — شعار شروط اللوحة: تعريف المجتمع، النافذة،
                       التغطية، المصدر. مطلوب في «شرط الإقفال الأول». */}
                  <section
                    data-testid="hub-conditions-banner"
                    dir="rtl"
                    style={{
                      marginTop: 24, padding: "14px 18px",
                      borderRadius: 14,
                      background: "linear-gradient(to left, #F0F9FF, #ECFDF5)",
                      border: "1px solid rgba(6,95,70,0.20)",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 12, fontSize: 12, color: "#065F46",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10, color: "#0F766E", fontWeight: 700 }}>مجتمع القياس</div>
                      <div data-testid="hub-conditions-population" style={{ marginTop: 2, fontWeight: 600 }}>
                        {dist?.population_note
                          ? "كل عميل له شراء واحد في تاريخه"
                          : "—"}
                        {" · "}
                        <span style={{ fontFamily: "monospace" }}>
                          {(dist?.total_customers ?? 0).toLocaleString("en-US")} عميلاً
                        </span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#0F766E", fontWeight: 700 }}>النافذة</div>
                      <div data-testid="hub-conditions-window" style={{ marginTop: 2, fontWeight: 600 }}>
                        {dist?.window_days ?? 90} يوماً — للحدود v/s/aov فقط.
                        recency محسوب من كامل التاريخ.
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#0F766E", fontWeight: 700 }}>المصدر</div>
                      <div data-testid="hub-conditions-source" style={{ marginTop: 2, fontWeight: 600, fontFamily: "monospace", fontSize: 11 }}>
                        {dist?.population_source
                          || "pos_sales · source=retm · total_price>0"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#0F766E", fontWeight: 700 }}>وضع الأرقام المالية</div>
                      <div data-testid="hub-conditions-money-mode" style={{ marginTop: 2, fontWeight: 600 }}>
                        صافٍ بلا ضريبة — يُقسم على (1+VAT) من tenant_settings.
                      </div>
                    </div>
                  </section>
                </CustomerFilterProvider>
              )}

              {activeTab === "directory" && (
                <CustomerFilterProvider>
                <LegacyTabWrapper testid="hub-directory-panel">
                  {/* فلتر مستقل لتبويب الدليل — مع الفرع (repeat-rate يدعمه) */}
                  <CustomerFilterBar />
                  {/* 2026-08-31 — تصدير قائمة دليل العملاء كما هي (Excel). */}
                  <DirectoryExportButton />
                  {/* Phase 7f — إعادة ترتيب بصري لثلاث طبقات منطقية.
                       بلا حذف أي مكوّن أو تغيير منطق/endpoint — DOM فقط. */}

                  {/* الطبقة أ — نظرة عامة على القاعدة (الصف العلوي الرسمي:
                       إجمالي العملاء ← معدل التكرار ← تسجيل الجدد ← معدل الاحتفاظ) */}
                  <LayerHeader index="a" title="① نظرة عامة على القاعدة"
                    subtitle="كم عميلًا لديّ وكيف حالهم؟ (إجمالي · تكرار · جدد · احتفاظ)" />
                  <Suspense fallback={<TabLoader />}>
                    <LegacyCustomersHeader />
                  </Suspense>
                  {/* بطاقة «معدل تكرار العملاء» — نفس فلتر الفرع/التاريخ الموحّد. */}
                  <RepeatRateCard />
                  {/* بطاقة «معدل تسجيل العملاء الجدد» (عرض فقط). */}
                  <NewRegistrationsCard />
                  {/* بطاقة «معدّل الاحتفاظ» الرئيسية (مُبرَزة من MeasuredMetrics). */}
                  <RetentionRateCard />
                  {/* C1 — استرداد المفقودين (نفس دالة بطاقات «عملاء كل فرع»). */}
                  <RecoveryRateCard />
                  <Suspense fallback={<TabLoader />}>
                    <SaudiMarketInsights />
                  </Suspense>

                  {/* الطبقة ب — الدليل الذكي للعملاء */}
                  <LayerHeader index="b" title="② الدليل الذكي للعملاء"
                    subtitle="مَن هم عملائي؟ — بحث وشرائح وتفاصيل" />
                  <Suspense fallback={<TabLoader />}>
                    <CustomersSmart customers={[]} spendingEnabled={true} campaigns={[]} showMarketInsights={false} showRecommender={false} />
                  </Suspense>

                  {/* الطبقة ج — سجل رتم الكامل (قابل للطي · مطوي افتراضيًا) */}
                  <LayerHeader index="c" title="③ سجل رتم الكامل"
                    subtitle="قائمة كل التاريخ — تُفتح عند الحاجة فقط"
                    action={
                      <button data-testid="rtm-log-toggle" type="button"
                        onClick={() => setRtmLogOpen((v) => !v)}
                        style={{
                          border: "1px solid rgba(6,95,70,0.25)", background: "#fff",
                          color: "#065F46", borderRadius: 999, padding: "8px 18px",
                          fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                        }}>
                        {rtmLogOpen ? "▲ طيّ السجل" : "▼ توسيع السجل الكامل"}
                      </button>
                    } />
                  {rtmLogOpen && (
                    <Suspense fallback={<TabLoader />}>
                      <POSCustomers />
                    </Suspense>
                  )}
                </LegacyTabWrapper>
                </CustomerFilterProvider>
              )}

              {activeTab === "expected" && (
                <LegacyTabWrapper testid="hub-expected-panel">
                  {!isCeoView && (
                    <ProjectedNotice>
                      هذه الشاشة تعرض توقّعات (متوقَّع، وليست حقائق).
                      استخدمها للاستئناس لا للقرار — الدقة الحقيقية المقاسة أدناه.
                    </ProjectedNotice>
                  )}
                  <PredictionAccuracyCard />
                  <Suspense fallback={<TabLoader />}>
                    <ExpectedToday />
                  </Suspense>
                </LegacyTabWrapper>
              )}

              {activeTab === "lab" && (
                <LegacyTabWrapper testid="hub-lab-panel">
                  <Suspense fallback={<TabLoader />}>
                    <CustomerLab />
                  </Suspense>
                </LegacyTabWrapper>
              )}

              {activeTab === "comms" && (
                <LegacyTabWrapper testid="hub-comms-panel">
                  <Suspense fallback={<TabLoader />}>
                    <CustomerSurveys />
                  </Suspense>
                </LegacyTabWrapper>
              )}

              {activeTab === "cap_approvals" && (
                <LegacyTabWrapper testid="hub-cap-approvals-panel">
                  <Suspense fallback={<TabLoader />}>
                    <SendCapApprovalsInbox />
                  </Suspense>
                </LegacyTabWrapper>
              )}
            </section>
          </>
        )}

        <footer style={{
          marginTop: 40, paddingTop: 20,
          borderTop: "1px solid rgba(61,42,28,0.12)",
          fontSize: 12, color: "#A8A29E", textAlign: "center",
        }}>
          كل رقم موثَّق في{" "}
          <a href="/data-sources" style={{ color: "#0F766E", textDecoration: "underline" }}>
            صفحة مصادر الأرقام
          </a>
        </footer>
      </div>
    </div>
  );
}

function TabLoader() {
  return (
    <div data-testid="hub-tab-loader" style={{
      padding: 60, textAlign: "center", color: "#78716C", fontSize: 13,
    }}>
      جارٍ تحميل الشاشة…
    </div>
  );
}

function LegacyTabWrapper({ children, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(61,42,28,0.12)",
        borderRadius: 18,
        padding: "20px 22px",
        boxShadow: "0 4px 16px rgba(61,42,28,0.06)",
      }}
    >
      {children}
    </div>
  );
}

function ProjectedNotice({ children }) {
  return (
    <div
      data-testid="hub-projected-notice"
      style={{
        marginBottom: 14, padding: "10px 14px", borderRadius: 10,
        background: "rgba(180, 83, 9, 0.10)", color: "#B45309",
        border: "1px solid rgba(180, 83, 9, 0.35)",
        fontSize: 12, lineHeight: 1.7,
      }}
    >
      ⚠️ {children}
    </div>
  );
}
